import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Layers } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, StatusBadge, fmtUSD, fmtDate } from "@/components/ui-bits";
import { Field } from "@/pages/Login";

export default function Funding() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/funding/mine");
    setPlans(data);
    refresh();
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post("/funding/create", { amount: Number(amount) });
      toast.success("Contribution started");
      setAmount("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const activePlans = plans.filter((p) => p.status === "active");
  const inactivePlans = plans.filter((p) => p.status !== "active");

  return (
    <div>
      <PageHeader title="Contribution" subtitle="Allocate USDT from your wallet into the Participation Program." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        <div className="glass-strong p-6 lg:col-span-1 neon-glow">
          <div className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-bold mb-1">Wallet Balance</div>
          <div className="font-display text-3xl font-black">{fmtUSD(user?.wallet_balance)}</div>
          <div className="mt-5 space-y-3">
            <Field label="Contribution Amount (USDT)" type="number" value={amount} onChange={setAmount} testid="funding-amount" />
            <button
              onClick={start}
              disabled={busy}
              data-testid="funding-submit"
              className="w-full py-3 rounded-lg bg-[#00F0FF] text-[#060B19] font-bold hover:bg-[#00D4E0] hover:shadow-[0_0_24px_rgba(0,240,255,0.45)] transition-all disabled:opacity-50"
            >
              {busy ? "Starting…" : "Start Contribution"}
            </button>
          </div>
          <div className="mt-5 text-xs text-[#94A3B8] leading-relaxed border-t border-white/5 pt-4">
            Contributions are allocated to active Participation Programs and tracked per plan. Each plan completes once it reaches its program milestone, after which it is closed automatically.
          </div>
        </div>

        <div className="glass p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <Layers size={18} className="text-[#00F0FF]" />
            <h3 className="font-display text-lg font-bold">Active Plans</h3>
          </div>
          <div className="space-y-3">
            {activePlans.length === 0 && <div className="text-[#475569] text-sm py-6 text-center">No active contribution plans yet</div>}
            {activePlans.map((p) => <PlanCard key={p.id} p={p} />)}
          </div>

          {inactivePlans.length > 0 && (
            <div className="mt-6 pt-5 border-t border-white/5">
              <h3 className="font-display text-base font-bold mb-3 text-[#94A3B8]">Inactive / Closed Plans</h3>
              <div className="space-y-3 opacity-80">
                {inactivePlans.map((p) => <PlanCard key={p.id} p={p} />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PlanCard({ p }) {
  const pct = Math.min(100, (Number(p.roi_generated) / Number(p.max_roi || 1)) * 100);
  return (
    <div className="border border-white/10 rounded-lg p-4 hover:border-[#00F0FF]/30 transition-all" data-testid={`plan-${p.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="font-mono text-lg font-bold">{fmtUSD(p.amount)}</div>
          <div className="text-xs text-[#94A3B8]">Started {fmtDate(p.created_at)}</div>
        </div>
        <StatusBadge status={p.status} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-xs">
        <div>
          <div className="text-[#475569] uppercase tracking-wider">Plan Amount</div>
          <div className="font-mono text-white mt-0.5">{fmtUSD(p.plan_amount ?? p.amount)}</div>
        </div>
        <div>
          <div className="text-[#475569] uppercase tracking-wider">Withdrawable</div>
          <div className="font-mono text-[#00F0FF] mt-0.5">{fmtUSD(p.withdrawable_amount ?? p.amount)}</div>
        </div>
        <div>
          <div className="text-[#475569] uppercase tracking-wider">Start Date</div>
          <div className="text-white mt-0.5">{fmtDate(p.created_at)}</div>
        </div>
        <div>
          <div className="text-[#475569] uppercase tracking-wider">Maturity</div>
          <div className="text-white mt-0.5">{fmtDate(p.maturity_at)}</div>
        </div>
      </div>
      {p.status === "active" && (
        <div className="mt-3">
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-[#94A3B8]">Program Progress</span>
            <span className="font-mono text-[#00F0FF]">{pct.toFixed(1)}%</span>
          </div>
          <div className="h-2 bg-[#0A132A] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#00F0FF] to-[#007AFF]" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}
